/* eslint-disable @typescript-eslint/no-explicit-any */
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Types
export interface User {
  id: string;
  email: string;
  full_name?: string;
  is_admin: boolean;
  auth_id?: string;
  created_at: string;
  updated_at: string;
  subscription_tier?: 'free' | 'individual' | 'portfolio_pro' | 'professional';
  subscription_status?: 'active' | 'trialing' | 'expired' | 'cancelled' | 'suspended';
  subscription_start_date?: string;
  subscription_end_date?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
}

export interface AuthState {
  user: User | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  session: any;
  isLoading: boolean;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface SignUpData {
  email: string;
  password: string;
  fullName?: string;
}

// Auth Service Class
class AuthService {
  private supabase: SupabaseClient | null = null;
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

    // Debug logging for environment variables
    console.log('Supabase Configuration:', {
      hasUrl: !!supabaseUrl,
      hasKey: !!supabaseAnonKey,
    });

    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('❌ Missing Supabase environment variables! Authentication will not work.');
      console.error('Required variables: REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY');
      this.isInitialized = false;
      return;
    }

    try {
      this.supabase = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          detectSessionInUrl: true,
          autoRefreshToken: true,
        },
      });

      this.isInitialized = true;
      console.log('✅ Supabase client initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Supabase client:', error);
      this.isInitialized = false;
    }
  }

  private ensureInitialized(): SupabaseClient {
    if (!this.isInitialized || !this.supabase) {
      throw new Error('Authentication service not available. Please check your configuration.');
    }
    return this.supabase;
  }

  // Auth operations
  async signIn(data: SignInData) {
    const client = this.ensureInitialized();

    console.log('🔐 Attempting to sign in with Supabase...');

    try {
      const { data: authData, error } = await client.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        console.error('❌ Supabase sign in error:', error);
        throw error;
      }

      console.log('✅ Sign in successful');
      return authData;
    } catch (error) {
      console.error('❌ Sign in failed:', error);
      throw error;
    }
  }

  async signUp(data: SignUpData) {
    const client = this.ensureInitialized();

    console.log('📝 Attempting to sign up with Supabase...');

    try {
      const { data: authData, error } = await client.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
          },
        },
      });

      if (error) {
        console.error('❌ Supabase sign up error:', error);
        throw error;
      }

      console.log('✅ Sign up successful');
      return authData;
    } catch (error) {
      console.error('❌ Sign up failed:', error);
      throw error;
    }
  }

  async signOut() {
    const client = this.ensureInitialized();

    console.log('👋 Signing out...');

    try {
      const { error } = await client.auth.signOut();
      if (error) {
        console.error('❌ Sign out error:', error);
        throw error;
      }

      console.log('✅ Sign out successful');
    } catch (error) {
      console.error('❌ Sign out failed:', error);
      throw error;
    }
  }

  async getSession() {
    const client = this.ensureInitialized();

    try {
      const {
        data: { session },
        error,
      } = await client.auth.getSession();
      if (error) {
        console.error('❌ Error getting session:', error);
        return null;
      }
      return session;
    } catch (error) {
      console.error('❌ Failed to get session:', error);
      return null;
    }
  }

  async getUser(token?: string) {
    const client = this.ensureInitialized();

    try {
      const {
        data: { user },
        error,
      } = await client.auth.getUser(token);
      if (error) {
        console.error('❌ Error getting user:', error);
        return null;
      }
      return user;
    } catch (error) {
      console.error('❌ Failed to get user:', error);
      return null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAuthStateChange(callback: (event: string, session: any) => void) {
    const client = this.ensureInitialized();

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange(callback);
    return subscription;
  }

  // Token refresh with retry logic
  async refreshSession(retryCount = 0): Promise<any> {
    const client = this.ensureInitialized();
    const maxRetries = 3;

    try {
      console.log(`🔄 Attempting to refresh session (attempt ${retryCount + 1}/${maxRetries})...`);

      const { data, error } = await client.auth.refreshSession();

      if (error) {
        console.error('❌ Session refresh error:', error);

        // Retry on network errors
        if (retryCount < maxRetries - 1 && this.isNetworkError(error)) {
          console.log('⏳ Network error detected, retrying in 1s...');
          await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)));
          return this.refreshSession(retryCount + 1);
        }

        throw error;
      }

      console.log('✅ Session refreshed successfully');
      return data;
    } catch (error) {
      console.error('❌ Failed to refresh session:', error);
      throw error;
    }
  }

  // Check if token is expired or about to expire (within 60 seconds)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTokenExpired(session?: any): boolean {
    if (!session?.expires_at) {
      return true;
    }

    const expiresAt = session.expires_at * 1000; // Convert to milliseconds
    const now = Date.now();
    const bufferTime = 60 * 1000; // 60 seconds buffer

    return now >= expiresAt - bufferTime;
  }

  // Get a valid session, refreshing if necessary
  async getValidSession(): Promise<any> {
    const client = this.ensureInitialized();

    try {
      const {
        data: { session },
        error,
      } = await client.auth.getSession();

      if (error) {
        console.error('❌ Error getting session:', error);
        return null;
      }

      if (!session) {
        console.log('⚠️ No session available');
        return null;
      }

      // Check if token is expired or about to expire
      if (this.isTokenExpired(session)) {
        console.log('⏰ Token expired or about to expire, refreshing...');
        const refreshResult = await this.refreshSession();
        return refreshResult.session;
      }

      return session;
    } catch (error) {
      console.error('❌ Failed to get valid session:', error);
      return null;
    }
  }

  // Helper to detect network errors
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isNetworkError(error: any): boolean {
    const errorMessage = error?.message?.toLowerCase() || '';
    return (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('timeout') ||
      error?.status === 0
    );
  }

  // Helper methods
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getApiHeaders(session?: any): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    return headers;
  }

  isAvailable(): boolean {
    return this.isInitialized && this.supabase !== null;
  }

  // Create a fallback user from auth data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createFallbackUser(authUser: any): User {
    return {
      id: authUser.id,
      email: authUser.email || '',
      full_name: authUser.user_metadata?.full_name || authUser.email?.split('@')[0] || 'User',
      is_admin: authUser.email === 'val.petrov@gmail.com',
      auth_id: authUser.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }
}

// Demo login function
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function demoLogin(): Promise<{ user: User; session: any; is_demo: boolean }> {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  console.log('🎭 Attempting demo login...');

  try {
    const response = await fetch(`${apiUrl}/api/auth/demo-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Demo login failed');
    }

    const data = await response.json();
    console.log('✅ Demo login successful');

    return data;
  } catch (error) {
    console.error('❌ Demo login failed:', error);
    throw error;
  }
}

// Demo key login function
export async function demoKeyLogin(
  demoKey: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<{ user: User; session: any; is_demo: boolean }> {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  console.log('🔑 Attempting demo key login...');

  try {
    const response = await fetch(`${apiUrl}/api/auth/demo-key-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ demoKey }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Demo key login failed');
    }

    const data = await response.json();
    console.log('✅ Demo key login successful');

    return data;
  } catch (error) {
    console.error('❌ Demo key login failed:', error);
    throw error;
  }
}

// Check if current session is demo
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDemoSession(session: any): boolean {
  return session?.access_token?.startsWith('demo_') || false;
}

// Export singleton instance
export const authService = new AuthService();
